import classNames from 'classnames'

import NumberInput from '../../../../common/components/Form/NumberInput.js'
import InsuranceCost from './InsuranceCost.js'
import {getState, useSelector} from '../../../../store.js'
import EnableLabelProperty from '../EnableLabelProperty.js'
import Boolean from '../../../../common/components/Boolean.js'
import {
  canHaveInsuranceSelector,
  insuranceErrorMessageSelector,
  isLabelPropertyEnabledSelector,
  onLabelPropertyValueChange,
  theyCanHaveInsuranceSelector,
} from '../../../../data/labelInfos/index.js'
import useLabelProperty, {
  EMPTY_DEPENDENT_PROPERTIES_FUNC,
} from '../useLabelProperty.js'
import ConfigCheckbox, {
  onCheckboxLabelPropertyEnable,
} from '../ConfigCheckbox.js'
import {
  ENABLE_MODE__FULL,
  useLabelConfigContext,
} from '../../LabelConfigContext.js'
import {SHIPPER_VENDORS} from '../../../../common/constants/ShipperNames.js'
import {useEffect} from 'react'

const LABEL_PROPERTY_INCLUDE_INSURANCE = 'include_insurance'
const LABEL_PROPERTY_INSURED_VALUE = 'insured_value'

function includeInsuranceLabelProperties(shipperType, labelInfoID) {
  const canHaveInsurance = theyCanHaveInsuranceSelector(getState(), {
    labelInfoID,
  })
  return canHaveInsurance ? [LABEL_PROPERTY_INCLUDE_INSURANCE] : []
}

const INCLUDE_INSURANCE_VALID_PROPERTIES = [LABEL_PROPERTY_INCLUDE_INSURANCE]

function insuredValueLabelProperties(shipperType, labelInfoID) {
  const canHaveInsurance = theyCanHaveInsuranceSelector(getState(), {
    labelInfoID,
  })

  return canHaveInsurance ? [LABEL_PROPERTY_INSURED_VALUE] : []
}

function insuredValueDependentProperties() {
  return [LABEL_PROPERTY_INCLUDE_INSURANCE]
}

const INSURED_VALUE_VALID_PROPERTIES = [LABEL_PROPERTY_INSURED_VALUE]

export default function Insurance({
  includeInsurance,
  toggle,
  insuredValue,
  setInsuredValue,
  insuranceCost,
}) {
  const {labelInfoID, shipperType, isLabelEnableMode, isSummary, isPickMode} =
    useLabelConfigContext()
  const [includeInsurancePropertySpecs, insuredValuePropertySpecs] =
    Insurance.propertySpecList
  const [includeInsuranceLabelProperty] =
    includeInsurancePropertySpecs.labelPropertiesFunc(shipperType, labelInfoID)
  const [insuredValueLabelProperty] =
    insuredValuePropertySpecs.labelPropertiesFunc(shipperType, labelInfoID)

  const canHaveInsurance = useSelector((state) =>
    canHaveInsuranceSelector(state, {labelInfoID, shipperType}),
  )
  const theyCanHaveInsurance = useSelector((state) =>
    theyCanHaveInsuranceSelector(state, {labelInfoID, shipperType}),
  )
  const insuranceErrorMessage = useSelector((state) =>
    insuranceErrorMessageSelector(state, {labelInfoID}),
  )

  const isIncludeInsuranceEnabled = useSelector((state) =>
    isLabelPropertyEnabledSelector(state, {
      labelInfoID,
      labelProperty: includeInsuranceLabelProperty,
    }),
  )

  useLabelProperty({
    labelProperty: insuredValueLabelProperty,
    propertySpecs: insuredValuePropertySpecs,
  })

  const isInsuredValueEnabled = useSelector((state) =>
    isLabelPropertyEnabledSelector(state, {
      labelInfoID,
      labelProperty: insuredValueLabelProperty,
    }),
  )
  // hide if
  // single shipper can't have insurance
  // multi shipper all can't have insurance
  const hide = !shipperType ? !theyCanHaveInsurance : !canHaveInsurance

  useEffect(() => {
    // the "hide" value changes when shipper selection changes
    // but it also runs when the component is mounted, so be careful
    // this function will add "missing" properties it thinks are needed
    onLabelPropertyValueChange(labelInfoID, LABEL_PROPERTY_INCLUDE_INSURANCE)
  }, [hide])

  return (
    <>
      <ConfigCheckbox
        label={isPickMode ? 'Insurance' : 'Insure the package'}
        onChange={toggle}
        labelProperty={includeInsuranceLabelProperty}
        propertySpecs={includeInsurancePropertySpecs}
        hide={hide}
      />
      {!hide && !(isPickMode && !isIncludeInsuranceEnabled) && !isSummary && (
        <li className="flex-item list__item--shipping-options has-sub-value">
          <div className="wrap--edit-preset-form-input">
            <div
              className={classNames('wrap--insurance-amt wrap--edit-mode', {
                'op-50': !includeInsurance,
              })}
            >
              <ul className="list">
                <li className="list__item--no-style flex flex-wrap flex-wrap-margin-10 margin-bottom-7">
                  <div className="flex-item">
                    <label className="label--sm" htmlFor="insured_value">
                      Value
                    </label>
                    <NumberInput
                      id="insured_value"
                      value={insuredValue || ''}
                      onChange={(value) => setInsuredValue(`${value}`)}
                    />
                    {insuranceErrorMessage && (
                      <small className="error">{insuranceErrorMessage}</small>
                    )}
                  </div>
                </li>
                <li className="list__item--no-style fs-n1 margin-bottom-5">
                  <InsuranceCost
                    insuranceCost={insuranceCost}
                    includeInsurance={includeInsurance}
                  />
                </li>
                {isLabelEnableMode && isIncludeInsuranceEnabled && (
                  <li className="list__item--no-style fs-n1 margin-bottom-5">
                    <div>
                      {includeInsurance ? (
                        <div>
                          {!isInsuredValueEnabled && (
                            <em>Value of the order will be insured</em>
                          )}
                        </div>
                      ) : (
                        <Boolean value={includeInsurance} />
                      )}
                    </div>
                  </li>
                )}
              </ul>
              <div className="divider--top sm">
                <small className="input--help lh-md block">
                  {
                    'By using Shipsurance’s discounted insurance rates, you agree to the '
                  }
                  <a
                    href="http://support.ordoro.com/shipsurance-terms-conditions/"
                    title="Shipsurances terms and conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    terms and conditions
                  </a>
                  {'.'}
                </small>
              </div>
            </div>
          </div>
          {isIncludeInsuranceEnabled && (
            <EnableLabelProperty
              labelProperty={insuredValueLabelProperty}
              overrideEnableMode={ENABLE_MODE__FULL}
            />
          )}
        </li>
      )}
    </>
  )
}

Insurance.propertySpecList = [
  {
    validShipperTypes: SHIPPER_VENDORS,
    labelPropertiesFunc: includeInsuranceLabelProperties,
    dependentPropertiesFunc: EMPTY_DEPENDENT_PROPERTIES_FUNC,
    validLabelPropertiesFunc() {
      return INCLUDE_INSURANCE_VALID_PROPERTIES
    },
    onLabelPropertyEnable: onCheckboxLabelPropertyEnable,
  },
  {
    validShipperTypes: SHIPPER_VENDORS,
    labelPropertiesFunc: insuredValueLabelProperties,
    dependentPropertiesFunc: insuredValueDependentProperties,
    validLabelPropertiesFunc() {
      return INSURED_VALUE_VALID_PROPERTIES
    },
  },
]
